import { Card, Table, Input, Button, DatePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";

import Loader from "../../loader/loader";
import PageTitle from "../../page-header/PageHeader";
import ColVisibilityDropdown from "../../Shared/ColVisibilityDropdown";
import { CsvLinkBtn } from "../../UI/CsvLinkBtn";
import { loadAllTaskHistory } from "../../../redux/rtk/features/taskStatusHistory/taskStatusHistorySlice";

function GetDailyStatusReport() {
  const [columnsToShow, setColumnsToShow] = useState([]);
  const { list = [], loading } = useSelector((state) => state?.taskHistory);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({});
  const searchInput = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAllTaskHistory());
  }, [dispatch]);

  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  // Apply filters whenever they change or when data loads
  useEffect(() => {
    if (!list || !list.length) {
      setFilteredData([]);
      return;
    }

    // Start with all data
    let result = [...list];

    // Apply each active filter
    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;

      switch (key) {
        case "UserName":
          result = result.filter((item) => {
            const user = item?.task?.assignedTask?.[0]?.user;
            const fullName = `${user?.firstName || ""} ${
              user?.lastName || ""
            }`.toLowerCase();
            return fullName.includes(value.toLowerCase());
          });
          break;
        case "project":
          result = result.filter((item) => {
            const project = item?.task?.project?.name?.toLowerCase() || "";
            return project.includes(value.toLowerCase());
          });
          break;
        case "milestone":
          result = result.filter((item) => {
            const milestone = item?.task?.milestone?.name?.toLowerCase() || "";
            return milestone.includes(value.toLowerCase());
          });
          break;
        case "taskName":
          result = result.filter((item) => {
            const taskName = item?.task?.name?.toLowerCase() || "";
            return taskName.includes(value.toLowerCase());
          });
          break;
        case "ActualStartDateTime":
          result = result.filter((item) => {
            if (!item.ActualStartDateTime) return false;
            const dateValue = dayjs(item.ActualStartDateTime).format(
              "YYYY-MM-DD"
            );
            return dateValue === value;
          });
          break;
        case "ActualEndDateTime":
          result = result.filter((item) => {
            if (!item.ActualEndDateTime) return false;
            const dateValue = dayjs(item.ActualEndDateTime).format(
              "YYYY-MM-DD"
            );
            return dateValue === value;
          });
          break;
        case "note":
          result = result.filter((item) => {
            const note = item?.note?.toLowerCase() || "";
            return note.includes(value.toLowerCase());
          });
          break;
        case "taskStatus":
          result = result.filter((item) => {
            const status = item?.taskStatus?.name?.toLowerCase() || "";
            return status.includes(value.toLowerCase());
          });
          break;
        default:
          break;
      }
    });

    setFilteredData(result);
  }, [list, filters]);

  const handleSearch = (value, dataIndex) => {
    setFilters((prev) => ({
      ...prev,
      [dataIndex]: value,
    }));
  };

  const handleReset = (dataIndex) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={placeholder || `Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedKeys(value ? [value] : []);
            handleSearch(value, dataIndex);
          }}
          onPressEnter={(e) => e.stopPropagation()}
          style={{ marginBottom: 8, display: "block" }}
          autoFocus
        />
        <Button
          onClick={() => {
            clearFilters && clearFilters();
            handleReset(dataIndex);
          }}
          size="small"
          style={{ width: "100%" }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    // We're not using the built-in filtering, so this is just a dummy function
    onFilter: () => true,
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const getDateColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <DatePicker
          style={{ marginBottom: 8, width: "100%" }}
          value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
          onChange={(date, dateString) => {
            setSelectedKeys(dateString ? [dateString] : []);
            handleSearch(dateString, dataIndex);
          }}
        />
        <Button
          onClick={() => {
            clearFilters && clearFilters();
            handleReset(dataIndex);
          }}
          size="small"
          style={{ width: "100%" }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    // We're not using the built-in filtering, so this is just a dummy function
    onFilter: () => true,
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const columns = [
    {
      id: 1,
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
    },
    {
      id: 2,
      title: "User Name",
      dataIndex: "task",
      key: "UserName",
      ...getColumnSearchProps("UserName", "Search by name"),
      render: (task) => {
        const user = task?.assignedTask?.[0]?.user;
        const fullName = `${user?.firstName || ""} ${
          user?.lastName || ""
        }`.trim();
        return fullName || "--";
      },
    },
    {
      id: 3,
      title: "Project",
      dataIndex: "task",
      key: "project",
      ...getColumnSearchProps("project", "Search project"),
      render: (task) => task?.project?.name || "--",
    },
    {
      id: 4,
      title: "Milestone",
      dataIndex: "task",
      key: "milestone",
      ...getColumnSearchProps("milestone", "Search milestone"),
      render: (task) => task?.milestone?.name || "--",
    },
    {
      id: 5,
      title: "Priority",
      dataIndex: "task",
      key: "priority",
      render: (task) => task?.priority?.name || "--",
    },
    {
      id: 6,
      title: "Task",
      dataIndex: "task",
      key: "taskName",
      ...getColumnSearchProps("taskName", "Search task name"),
      render: (task) => task?.name || "--",
    },
    {
      id: 7,
      title: "Start Date",
      dataIndex: "ActualStartDateTime",
      key: "ActualStartDateTime",
      ...getDateColumnSearchProps("ActualStartDateTime", "Search start date"),
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "--"),
    },
    {
      id: 8,
      title: "End Date",
      dataIndex: "ActualEndDateTime",
      key: "ActualEndDateTime",
      ...getDateColumnSearchProps("ActualEndDateTime", "Search end date"),
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : "--"),
    },
    {
      id: 9,
      title: "Note",
      dataIndex: "note",
      key: "note",
      ...getColumnSearchProps("note", "Search notes"),
      render: (note) => note?.trim() || "--",
    },
    {
      id: 10,
      title: "Task Status",
      dataIndex: "taskStatus",
      key: "taskStatus",
      ...getColumnSearchProps("taskStatus", "Search status"),
      render: (status) => status?.name || "--",
    },
  ];

  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  const addKeys = (arr) =>
    arr.map((i, index) => ({ ...i, key: i.id, serial: index + 1 }));

  // Determine which data to show (filtered or all)
  const dataToShow = Object.keys(filters).length > 0 ? filteredData : list;

  return (
    <>
      <PageTitle title="Back" />
      {!loading ? (
        <Card className="mt-5">
          <div className="text-center my-2 flex justify-between">
            <h5 className="department-list-title text-color-2 text-xl mb-2">
              Daily Status Report
            </h5>
            {list && (
              <div>
                <CsvLinkBtn>
                  <CSVLink
                    data={list}
                    className="btn btn-dark btn-sm mb-1"
                    filename="dailyStatusReport"
                  >
                    Download CSV
                  </CSVLink>
                </CsvLinkBtn>
              </div>
            )}
          </div>

          {list && (
            <div style={{ marginBottom: "30px" }}>
              <ColVisibilityDropdown
                options={columns}
                columns={columns}
                columnsToShowHandler={columnsToShowHandler}
              />
            </div>
          )}

          <Table
            scroll={{ x: true }}
            loading={!list || loading}
            columns={columnsToShow}
            dataSource={dataToShow ? addKeys(dataToShow) : []}
          />
        </Card>
      ) : (
        <Loader />
      )}
    </>
  );
}

export default GetDailyStatusReport;
